import {makeRequest} from '@ELib/utilities/requests';

import {LogicReturnType, PasswordResetForm, ResetError} from './types';

let localBaseURL = '';
const startResetURL =
  '/auth/rest/public/self-service/flows/default-password-reset-flow/select/';
const requestOTPURL = '/auth/rest/public/self-service/username/identify';
const abortResetFlowURL = '/auth/rest/public/self-service/flow/';
const abortLoginURL = '/auth/rest/public/authentication/';
const setPasswordURL = '/auth/rest/public/self-service/password/set/';
const checkOTPURL =
  '/auth/rest/public/self-service/verification/email/otp/check/';
const passwordPolicyCheckURL = '/auth/rest/public/password/policy/check';

type StandardAnswer = {
  data?: {attributes?: {nextStep?: string}};
};

export const setLocalBaseURL = (url: string): void => {
  localBaseURL = url;
};

const handleResponse = async (
  response: Response | undefined,
  callback: (status: number, response: StandardAnswer) => LogicReturnType
): LogicReturnType => {
  if (response) {
    try {
      const answer = (await response.json()) as StandardAnswer;
      return await callback(response.status, answer);
    } catch {
      return ResetError.UNEXPECTED;
    }
  }
  return ResetError.UNEXPECTED;
};

export const abortLogin = async (): LogicReturnType => {
  const resp = await makeRequest(
    `${localBaseURL}${abortLoginURL}`,
    undefined,
    'DELETE'
  );
  if (resp?.status === 204) {
    return undefined;
  }
  return ResetError.UNEXPECTED;
};

export const abortPasswordReset = async (): LogicReturnType => {
  const resp = await makeRequest(
    `${localBaseURL}${abortResetFlowURL}`,
    undefined,
    'DELETE'
  );
  if (resp?.status === 204) {
    return undefined;
  }
  return ResetError.UNEXPECTED;
};

export const initResetWithOTP = async (
  form: PasswordResetForm
): LogicReturnType => {
  const initResetStatus = await makeRequest(`${localBaseURL}${startResetURL}`);
  if (initResetStatus?.status !== 200) {
    return ResetError.RESET_NOT_POSSIBLE;
  }

  return await requestOTP(form);
};

export const requestOTP = async (form: PasswordResetForm): LogicReturnType => {
  const requestOTPForUser = await makeRequest(
    `${localBaseURL}${requestOTPURL}`,
    {
      username: form.username,
    }
  );

  if (requestOTPForUser?.status === 403) {
    return ResetError.INVALID_USER;
  } else if (requestOTPForUser?.status !== 200) {
    await abortPasswordReset();
    return ResetError.UNEXPECTED;
  }
  return undefined;
};

export const validateOTP = async (form: PasswordResetForm): LogicReturnType => {
  const validateOTPResponse = await makeRequest(
    `${localBaseURL}${checkOTPURL}`,
    {
      otp: form.otp,
    }
  );

  return await handleResponse(validateOTPResponse, async (status, answer) => {
    switch (status) {
      case 200:
        return undefined;
      case 403:
        if (answer.data?.attributes?.nextStep === 'EMAIL_OTP_EXPIRED') {
          if (!(await initResetWithOTP(form))) {
            return ResetError.RETRY_OTP;
          }
        }
        return ResetError.RESET_FAILED;
      case 400:
        return ResetError.INVALID_OTP;
      default:
        return ResetError.UNEXPECTED;
    }
  });
};

export const setNewPassword = async (
  form: PasswordResetForm
): LogicReturnType => {
  const newPasswordResponse = await makeRequest(
    `${localBaseURL}${setPasswordURL}`,
    {
      newPassword: form.password,
    }
  );
  if (newPasswordResponse?.status !== 200) {
    return ResetError.INVALID_PASSWORD;
  }
  return undefined;
};

export const passwordSatisfiesPolicy = async (
  password: string
): Promise<boolean> => {
  const validityResponse = await makeRequest(
    `${localBaseURL}${passwordPolicyCheckURL}`,
    {
      password,
    }
  );
  if (validityResponse?.status !== 200) {
    return false;
  }
  return true;
};
